import { Catalogue } from "../../types/catalogue.types";
import { mapStudioCatalogueFromApi } from "./catalogueSystemManaged";

export type ResolveCatalogueResult = {
  catalogue?: Catalogue;
  error?: string;
};

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
});

const apiBase = () => (process.env.REACT_APP_SERVERURL || "").replace(/\/+$/, "");

export const stubCatalogue = (id: string, name = "Catalogue"): Catalogue => ({
  id,
  name,
  products: 0,
  created: "",
  shared: 0,
  accessed: 0,
});

export const hasNamedCatalogueSeed = (
  catalogue: Catalogue | null | undefined,
  catalogueId: string
): catalogue is Catalogue =>
  Boolean(
    catalogue &&
      catalogue.id === catalogueId &&
      String(catalogue.name || "").trim()
  );

export const resolveCatalogue = async (
  catalogueId: string,
  seeded?: Catalogue
): Promise<ResolveCatalogueResult> => {
  const seed = seeded;
  if (hasNamedCatalogueSeed(seeded, catalogueId)) {
    return { catalogue: seeded };
  }

  const base = apiBase();
  const headers = authHeaders();
  if (!base) {
    return seed && seed.id === catalogueId
      ? { catalogue: seed }
      : { error: "Catalogue API is not configured." };
  }

  try {
    const productsRes = await fetch(
      `${base}/catalouge/${encodeURIComponent(catalogueId)}/products?offset=0&limit=1&sortBy=name&sortOrder=asc`,
      { headers }
    );
    const productsJson = await productsRes.json().catch(() => ({}));
    const fromProducts = productsJson?.data?.catalogue;
    if (fromProducts?.name) {
      return {
        catalogue: {
          ...stubCatalogue(catalogueId, fromProducts.name),
          ...mapStudioCatalogueFromApi(
            {
              _id: catalogueId,
              name: fromProducts.name,
              productCount: fromProducts.totalProducts,
              systemManaged: fromProducts.systemManaged,
              catalogueKind: fromProducts.catalogueKind,
              sharingMode: fromProducts.sharingMode,
            },
            catalogueId
          ),
        },
      };
    }
    if (productsRes.status === 404 || productsRes.status === 403) {
      return {
        error: productsJson?.message || "This catalogue is not available.",
      };
    }

    let offset = 0;
    const limit = 100;
    for (let page = 0; page < 5; page += 1) {
      const listRes = await fetch(
        `${base}/catalouge/catalogues?offset=${offset}&limit=${limit}&sortBy=createdAt&sortOrder=desc`,
        { headers }
      );
      const listJson = await listRes.json().catch(() => ({}));
      const docs = Array.isArray(listJson?.data?.docs) ? listJson.data.docs : [];
      const found = docs.find(
        (doc: any) => String(doc?._id || doc?.id) === catalogueId
      );
      if (found) {
        return { catalogue: mapStudioCatalogueFromApi(found, catalogueId) };
      }
      if (listJson?.data?.pagination?.hasMore !== true) {
        break;
      }
      offset += limit;
    }
  } catch {
    if (seed && seed.id === catalogueId) {
      return { catalogue: seed };
    }
    return { error: "Could not load this catalogue." };
  }

  if (seed && seed.id === catalogueId) {
    return { catalogue: seed };
  }
  return { catalogue: stubCatalogue(catalogueId) };
};
